import { useState, useEffect } from 'react';
import { Search, Plus, Eye, Calendar, MapPin, X, Truck, Package, Trash2, CheckCircle, Edit2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { notifyDispatchStatusChange, notifyNewDispatch } from '../../services/notificationService';
import { createDispatch } from '../../services/dispatchService';
import { ConfirmModal } from '../common/ConfirmModal';
import { logActivity } from '../../services/activityLogger';

interface Dispatch {
  id: string;
  dispatch_number: string;
  origin: string;
  destination: string;
  origin_date: string;
  destination_date: string;
  loaded_miles: number;
  empty_miles: number;
  driver_name: string;
  truck_number: string;
  trailer_number: string | null;
  load_ids: string[];
  is_tonu?: boolean;
  status: string;
  dispatcher_name: string;
  created_at?: string;
  sms_sent?: boolean;
  sms_status?: string;
}

interface Load {
  id: string;
  load_number: string;
  pickup_location?: string;
  delivery_location?: string;
  pickup_city?: string;
  pickup_state?: string;
  delivery_city?: string;
  delivery_state?: string;
  pickup_date: string;
  delivery_date: string;
  rate?: number;
  total_rate?: number;
  total_miles?: number;
  commodity?: string;
  status: string;
}

interface TruckWithDriver {
  id: string;
  unit_number: string;
  make: string;
  model: string;
  company?: string;
  driver: {
    id: string;
    first_name: string;
    last_name: string;
    driver_type?: 'company_driver' | 'owner_operator' | 'rent' | 'buy';
  };
}

export function DispatchesView() {
  const toast = useToast();
  const { user, profile } = useAuth();
  const [allDispatches, setAllDispatches] = useState<Dispatch[]>([]); // Store all dispatches for counts
  const [dispatches, setDispatches] = useState<Dispatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('active'); // Changed default to 'active' to exclude removed
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [truckFilter, setTruckFilter] = useState<string>('all');
  const [driverFilter, setDriverFilter] = useState<string>('all');
  const [createdByFilter, setCreatedByFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [availableLoads, setAvailableLoads] = useState<Load[]>([]);
  const [availableTrucks, setAvailableTrucks] = useState<TruckWithDriver[]>([]);
  const [dispatchStep, setDispatchStep] = useState<1 | 2 | 3>(1);
  const [loadSearchTerm, setLoadSearchTerm] = useState('');
  const [truckSearchTerm, setTruckSearchTerm] = useState('');
  const [selectedLoad, setSelectedLoad] = useState<string | null>(null);
  const [selectedTruck, setSelectedTruck] = useState<string | null>(null);
  const [dispatchType, setDispatchType] = useState<'active' | 'planned'>('active');
  const [driverPay, setDriverPay] = useState<number | ''>('');
  const [selectedDriverType, setSelectedDriverType] = useState<string | null>(null);
  const [dispatching, setDispatching] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [dispatchToDelete, setDispatchToDelete] = useState<Dispatch | null>(null);
  const [revenueStats, setRevenueStats] = useState({
    totalRevenue: 0,
    activeRevenue: 0,
    completedRevenue: 0
  });
  const [dispatchPermissions, setDispatchPermissions] = useState<Map<string, boolean>>(new Map());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [showCompletionConfirm, setShowCompletionConfirm] = useState(false);
  const [dispatchToComplete, setDispatchToComplete] = useState<string | null>(null);

  useEffect(() => {
    loadDispatches();
    loadRevenueStats();
  }, []); // Load once on mount

  // Filter dispatches when statusFilter changes
  useEffect(() => {
    filterDispatches();
  }, [statusFilter, allDispatches]);

  // Reset to page 1 when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, truckFilter, driverFilter, createdByFilter]);

  useEffect(() => {
    if (showCreateModal) {
      loadAvailableLoads();
      loadAvailableTrucks();
    }
  }, [showCreateModal]);

  // Filter dispatches based on status filter
  const filterDispatches = (dispatchesToFilter = allDispatches) => {
    let filtered = dispatchesToFilter;

    if (statusFilter === 'removed') {
      filtered = dispatchesToFilter.filter(d => d.status === 'removed');
    } else if (statusFilter === 'archived') {
      filtered = dispatchesToFilter.filter(d => d.status === 'archived');
    } else if (statusFilter === 'active') {
      filtered = dispatchesToFilter.filter(d => 
        d.status !== 'removed' && d.status !== 'delivered' && d.status !== 'archived'
      );
    } else if (statusFilter === 'delivered') {
      filtered = dispatchesToFilter.filter(d => d.status === 'delivered');
    } else if (statusFilter === 'all') {
      // 'all' shows everything except removed and archived
      filtered = dispatchesToFilter.filter(d => 
        d.status !== 'removed' && d.status !== 'archived'
      );
    }

    setDispatches(filtered);
  };

  const handleDispatchTypeChange = (type: 'active' | 'planned') => {
    setDispatchType(type);
  };

  // Auto-fill driver pay for company drivers when truck is selected
  useEffect(() => {
    if (selectedTruck && selectedLoad) {
      const selectedTruckData = availableTrucks.find(t => t.id === selectedTruck);
      const selectedLoadData = availableLoads.find(l => l.id === selectedLoad);
      const driverType = selectedTruckData?.driver?.driver_type;
      setSelectedDriverType(driverType || null);
      
      // For company drivers, auto-fill with load rate
      if (driverType === 'company_driver' && selectedLoadData) {
        setDriverPay(selectedLoadData.total_rate || 0);
      } else {
        // For owner operators, reset to empty so they must enter it
        setDriverPay('');
      }
    }
  }, [selectedTruck, selectedLoad]);

  const confirmCompleteDispatch = async () => {
    if (!dispatchToComplete) return;
    
    try {
      // First, get the dispatch to find the load_id
      const { data: dispatch, error: fetchError } = await supabase
        .from('dispatches')
        .select('load_id')
        .eq('id', dispatchToComplete)
        .single();

      if (fetchError) throw fetchError;

      console.log('Completing dispatch:', dispatchToComplete, 'with load:', dispatch?.load_id);

      // Update dispatch status to delivered
      // This will automatically trigger the revenue record creation via database trigger
      const { error: dispatchError } = await supabase
        .from('dispatches')
        .update({ 
          status: 'delivered',
          updated_by: user?.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', dispatchToComplete);

      if (dispatchError) {
        console.error('Dispatch update error:', dispatchError);
        throw dispatchError;
      }

      console.log('Dispatch status updated to delivered');

      // Get dispatch creator
      const currentDispatch = dispatches.find(d => d.id === dispatchToComplete);

      // Log activity
      await logActivity({
        action: 'update',
        resourceType: 'dispatch',
        resourceId: dispatchToComplete,
        resourceName: currentDispatch?.dispatch_number || dispatchToComplete,
        description: `Marked dispatch ${currentDispatch?.dispatch_number} as delivered`,
        oldData: { status: currentDispatch?.status },
        newData: { status: 'delivered' }
      });

      // Send notification to Superadmin/Admin
      const { data: allUsers } = await supabase
        .from('profiles')
        .select('id, role');

      if (allUsers) {
        for (const notifyUser of allUsers) {
          // Don't notify yourself
          if (notifyUser.id === user?.id) continue;

          const shouldNotify = 
            notifyUser.role === 'Superadmin' || 
            notifyUser.role === 'Admin';

          if (shouldNotify) {
            await notifyDispatchStatusChange({
              userId: notifyUser.id,
              dispatchId: dispatchToComplete,
              dispatchNumber: currentDispatch?.dispatch_number || dispatchToComplete,
              status: 'Delivered',
              driverName: currentDispatch?.driver_name || 'Unknown Driver'
            });
          }
        }
      }

      // Update load status to DELIVERED. Deliberately does NOT set
      // ready_for_billing: "Send to Accounting" on the load page is the
      // single action that flags the load for billing AND archives it, and
      // Dispatch/Dispatch Manager can now do it themselves. Setting the flag
      // here would swap that button for the green "Ready for Billing" badge,
      // leaving the load flagged but never archived.
      if (dispatch?.load_id) {
        const { error: loadError } = await supabase
          .from('loads')
          .update({ 
            status: 'DELIVERED',
            actual_delivery_date: new Date().toISOString()
          })
          .eq('id', dispatch.load_id);

        if (loadError) {
          console.error('Load update error:', loadError);
          throw loadError;
        }
        console.log('Load status updated to delivered');
      }

      // Verify revenue record was created
      const { data: revenueCheck, error: revenueError } = await supabase
        .from('revenue_records')
        .select('*')
        .eq('load_id', dispatch?.load_id)
        .maybeSingle();

      if (revenueError) {
        console.error('Revenue record check error:', revenueError);
      } else if (revenueCheck) {
        console.log('Revenue record created:', revenueCheck);
      } else {
        console.log('No revenue record found for this load');
      }

      toast.success('Dispatch completed successfully! Revenue recorded.');
      setShowCompletionConfirm(false);
      setDispatchToComplete(null);
      loadDispatches();
    } catch (error) {
      console.error('Error completing dispatch:', error);
      toast.error('Failed to complete dispatch');
      setShowCompletionConfirm(false);
      setDispatchToComplete(null);
    }
  };

  const handleDeleteDispatch = async () => {
    if (!dispatchToDelete) return;

    // Prevent removing already removed dispatches
    if (dispatchToDelete.status === 'removed') {
      toast.error('This dispatch is already removed');
      setShowDeleteConfirm(false);
      setDispatchToDelete(null);
      return;
    }

    try {
      // Mark dispatch as removed instead of deleting
      const { error: updateError } = await supabase
        .from('dispatches')
        .update({ 
          status: 'removed',
          updated_by: user?.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', dispatchToDelete.id);

      if (updateError) throw updateError;

      // Log activity
      await logActivity({
        action: 'delete',
        resourceType: 'dispatch',
        resourceId: dispatchToDelete.id,
        resourceName: dispatchToDelete.dispatch_number,
        description: `Deleted dispatch ${dispatchToDelete.dispatch_number}`,
        oldData: { 
          status: dispatchToDelete.status,
          driver_name: dispatchToDelete.driver_name,
          origin: dispatchToDelete.origin,
          destination: dispatchToDelete.destination
        },
        newData: { status: 'removed' }
      });

      // Get dispatch creator from database
      const { data: dispatchDetails } = await supabase
        .from('dispatches')
        .select('created_by')
        .eq('id', dispatchToDelete.id)
        .single();

      // Send notification to:
      // 1. Superadmin/Admin (they see everything)
      // 2. User who created the dispatch (if different from current user)
      const { data: allUsers } = await supabase
        .from('profiles')
        .select('id, role');

      if (allUsers) {
        for (const notifyUser of allUsers) {
          // Skip current user
          if (notifyUser.id === user?.id) continue;

          const shouldNotify = 
            notifyUser.role === 'Superadmin' || 
            notifyUser.role === 'Admin' ||
            notifyUser.id === dispatchDetails?.created_by;

          if (shouldNotify) {
            await notifyDispatchStatusChange({
              userId: notifyUser.id,
              dispatchId: dispatchToDelete.id,
              dispatchNumber: dispatchToDelete.dispatch_number,
              status: 'Removed',
              driverName: dispatchToDelete.driver_name
            });
          }
        }
      }

      // Get the dispatch with load_id
      const { data: dispatchData } = await supabase
        .from('dispatches')
        .select('load_id')
        .eq('id', dispatchToDelete.id)
        .single();

      // Update the load status back to 'BOOKED' and clear driver/truck assignment
      if (dispatchData?.load_id) {
        const { error: loadError } = await supabase
          .from('loads')
          .update({ 
            status: 'BOOKED',
            driver_id: null,
            truck_id: null
          })
          .eq('id', dispatchData.load_id);

        if (loadError) {
          console.error('Error updating load status:', loadError);
          // Continue with deletion even if load update fails
        }
      }

      toast.success('Dispatch removed successfully! Load is now available for dispatching.');
      setShowDeleteConfirm(false);
      setDispatchToDelete(null);
      loadDispatches();
    } catch (error) {
      console.error('Error deleting dispatch:', error);
      toast.error('Failed to delete dispatch');
      setShowDeleteConfirm(false);
      setDispatchToDelete(null);
    }
  };

  async function loadRevenueStats() {
    try {
      // Get all dispatches with their load revenue
      const dispatchesData = await fetchAllRows<any>(() => supabase
        .from('dispatches')
        .select('id, status, load_id, loads!dispatches_load_id_fkey(total_rate)'));

      // Get completed revenue from revenue_records
      const { data: completedRevenue } = await supabase
        .from('revenue_records')
        .select('total_revenue');

      const activeDispatches = dispatchesData?.filter(d => d.status?.toLowerCase() === 'active') || [];
      const activeRevenue = activeDispatches.reduce((sum, d) => {
        const loadRate = Array.isArray(d.loads) ? d.loads[0]?.total_rate : d.loads?.total_rate;
        return sum + (Number(loadRate) || 0);
      }, 0);

      const completedRevenueTotal = completedRevenue?.reduce((sum, r) => sum + Number(r.total_revenue), 0) || 0;
      const totalRevenue = activeRevenue + completedRevenueTotal;

      setRevenueStats({
        totalRevenue,
        activeRevenue,
        completedRevenue: completedRevenueTotal
      });
    } catch (error) {
      console.error('Error loading revenue stats:', error);
    }
  }

  // Check if user can complete a dispatch
  // Dispatch/Dispatch Manager can only complete if:
  // 1. They created the load, OR
  // 2. The dispatch is assigned to one of their drivers
  async function canCompleteDispatch(dispatchId: string): Promise<boolean> {
    // Superadmin and Admin can complete any dispatch
    if (profile?.role === 'Superadmin' || profile?.role === 'superadmin' || profile?.role === 'Admin' || profile?.role === 'admin') {
      return true;
    }

    // For Dispatch and Dispatch Manager roles
    if (profile?.role === 'Dispatch' || profile?.role === 'Dispatch Manager') {
      try {
        // Get dispatch with load and driver info
        const { data: dispatch } = await supabase
          .from('dispatches')
          .select(`
            id,
            load_id,
            driver_id,
            loads!dispatches_load_id_fkey (
              created_by
            )
          `)
          .eq('id', dispatchId)
          .single();

        if (!dispatch) return false;

        // Check if user created the load
        const loadCreatedBy = (dispatch.loads as any)?.created_by;
        if (loadCreatedBy === user?.id) {
          return true;
        }

        // Check if driver is assigned to this dispatcher
        if (dispatch.driver_id) {
          const { data: driver } = await supabase
            .from('drivers')
            .select('dispatcher_id')
            .eq('id', dispatch.driver_id)
            .single();

          if (driver?.dispatcher_id === user?.id) {
            return true;
          }
        }

        return false;
      } catch (error) {
        console.error('Error checking dispatch permission:', error);
        return false;
      }
    }

    // Other roles cannot complete dispatches
    return false;
  }

  async function loadDispatches() {
    try {
      // Build query based on status filter
      const data = await fetchAllRows<any>(() => supabase
        .from('dispatches')
        .select(`
          *,
          loads!dispatches_load_id_fkey (
            load_number,
            is_tonu,
            pickup_city,
            pickup_state,
            delivery_city,
            delivery_state,
            pickup_date,
            delivery_date,
            load_stops!load_stops_load_id_fkey(
              stop_type,
              stop_order,
              city,
              state,
              date
            )
          ),
          drivers!dispatches_driver_id_fkey (
            first_name,
            last_name
          ),
          trucks!dispatches_truck_id_fkey (
            unit_number
          ),
          created_by_profile:profiles!dispatches_created_by_fkey(full_name)
        `)
        .neq('status', 'home_time') // Exclude home_time dispatches
        .order('created_at', { ascending: false }));

      // Map the dispatch data to match our interface
      const mappedDispatches = (data || []).map((d: any) => {
        let pickupCity = d.loads?.pickup_city;
        let pickupState = d.loads?.pickup_state;
        let deliveryCity = d.loads?.delivery_city;
        let deliveryState = d.loads?.delivery_state;

        // Use load_stops data if available
        if (d.loads?.load_stops && d.loads.load_stops.length > 0) {
          const sortedStops = [...d.loads.load_stops].sort((a: any, b: any) => a.stop_order - b.stop_order);
          
          // Get first pickup stop
          const pickupStop = sortedStops.find((s: any) => s.stop_type === 'shipper');
          if (pickupStop) {
            pickupCity = pickupStop.city;
            pickupState = pickupStop.state;
          }
          
          // Get last delivery stop
          const deliveryStops = sortedStops.filter((s: any) => s.stop_type === 'consignee');
          if (deliveryStops.length > 0) {
            const lastDelivery = deliveryStops[deliveryStops.length - 1];
            deliveryCity = lastDelivery.city;
            deliveryState = lastDelivery.state;
          }
        }

        return {
          id: d.id,
          dispatch_number: d.dispatch_number,
          origin: pickupCity && pickupState ? `${pickupCity}, ${pickupState}` : 'N/A',
          destination: deliveryCity && deliveryState ? `${deliveryCity}, ${deliveryState}` : 'N/A',
          origin_date: d.loads?.pickup_date || null,
          destination_date: d.loads?.delivery_date || null,
          loaded_miles: 0,
          empty_miles: 0,
          driver_name: d.drivers ? `${d.drivers.first_name} ${d.drivers.last_name}` : 'N/A',
          truck_number: d.trucks?.unit_number || 'N/A',
          trailer_number: null,
          load_ids: d.loads ? [d.loads.load_number] : [],
          is_tonu: !!(d.loads && (d.loads as any).is_tonu),
          status: d.status,
          dispatcher_name: d.created_by_profile?.full_name || 'System',
          created_at: d.created_at,
          sms_sent: d.sms_sent,
          sms_status: d.sms_status,
        };
      });
      
      // Filter out dispatches for removed loads (those with _REMOVED_ in load number)
      const nonRemovedDispatches = mappedDispatches.filter(d => 
        !d.load_ids.some(loadNum => loadNum?.includes('_REMOVED_'))
      );
      
      setAllDispatches(nonRemovedDispatches);
      filterDispatches(nonRemovedDispatches);
      
      // Load permissions for active dispatches
      if (profile?.role === 'Dispatch' || profile?.role === 'Dispatch Manager') {
        const permissions = new Map<string, boolean>();
        for (const dispatch of mappedDispatches.filter(d => d.status === 'active')) {
          const canComplete = await canCompleteDispatch(dispatch.id);
          permissions.set(dispatch.id, canComplete);
        }
        setDispatchPermissions(permissions);
      }
      
      loadRevenueStats(); // Refresh revenue stats when dispatches change
    } catch (error) {
      console.error('Error loading dispatches:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadAvailableLoads() {
    try {
      // Get all booked loads
      const { data: bookedLoads, error: loadsError } = await supabase
        .from('loads')
        .select('*')
        .eq('status', 'booked')
        .order('pickup_date', { ascending: true });

      if (loadsError) throw loadsError;

      // Get all dispatches to find which loads are already dispatched.
      // This must be the complete set — a truncated read would show already
      // dispatched loads as available and invite a double dispatch.
      const dispatches = await fetchAllRows<any>(() => supabase
        .from('dispatches')
        .select('id, load_id')
        .neq('status', 'removed'));

      // Filter out loads that already have dispatches
      const dispatchedLoadIds = new Set((dispatches || []).map(d => d.load_id).filter(Boolean));
      const availableLoads = (bookedLoads || []).filter(load => !dispatchedLoadIds.has(load.id));

      setAvailableLoads(availableLoads);
    } catch (error) {
      console.error('Error loading available loads:', error);
      toast.error('Failed to load available loads');
    }
  }

  async function loadAvailableTrucks() {
    try {
      const { data: trucksData, error: trucksError } = await supabase
        .from('equipment_assignments')
        .select(`
          truck_id,
          trucks!inner(id, unit_number, make, model, dispatcher_id, companies(code, name)),
          driver_id,
          drivers!inner(id, first_name, last_name, driver_type)
        `)
        .eq('is_current', true);

      if (trucksData) {
        // Get all drivers with active dispatches
        const { data: activeDispatches } = await supabase
          .from('dispatches')
          .select('driver_id')
          .eq('status', 'active');

        const busyDriverIds = new Set(activeDispatches?.map(d => d.driver_id) || []);

        // Filter out trucks with drivers who have active dispatches
        const trucksWithDrivers = trucksData
          .filter((a: any) => a.trucks && a.drivers && !busyDriverIds.has(a.drivers.id))
          .map((a: any) => ({
            id: a.trucks.id,
            unit_number: a.trucks.unit_number,
            make: a.trucks.make,
            model: a.trucks.model,
            company: a.trucks.companies?.code || a.trucks.companies?.name,
            dispatcher_id: a.trucks.dispatcher_id,
            driver: {
              id: a.drivers.id,
              first_name: a.drivers.first_name,
              last_name: a.drivers.last_name,
              driver_type: a.drivers.driver_type,
            },
          }));
        
        // Sort trucks: current user's trucks first, then others
        const sortedTrucks = trucksWithDrivers.sort((a: any, b: any) => {
          const aIsMyTruck = a.dispatcher_id === user?.id;
          const bIsMyTruck = b.dispatcher_id === user?.id;
          
          if (aIsMyTruck && !bIsMyTruck) return -1;
          if (!aIsMyTruck && bIsMyTruck) return 1;
          
          // If both are mine or both are not mine, sort by unit number
          return a.unit_number.localeCompare(b.unit_number);
        });
        
        setAvailableTrucks(sortedTrucks);
      }
    } catch (error) {
      console.error('Error loading available trucks:', error);
      toast.error('Failed to load available trucks');
    }
  }

  async function handleCreateDispatch() {
    // Validate that we have all required data
    if (!selectedLoad || !selectedTruck || !dispatchType) {
      toast.error('Please complete all steps');
      return;
    }

    // Validate driver pay for owner operators
    const selectedTruckData = availableTrucks.find(t => t.id === selectedTruck);
    const driverType = selectedTruckData?.driver?.driver_type;
    if (driverType !== 'company_driver' && (driverPay === '' || driverPay <= 0)) {
      toast.error('Please enter driver pay amount for owner operator');
      return;
    }

    setDispatching(true);
    try {
      const load = availableLoads.find(l => l.id === selectedLoad);
      const truck = availableTrucks.find(t => t.id === selectedTruck);

      if (!load || !truck) return;

      // Try to find the rate confirmation PDF for this load
      let rateConfirmationPath: string | undefined;
      try {
        const { data: documents } = await supabase
          .from('documents')
          .select('file_path')
          .eq('load_id', load.id)
          .eq('file_type', 'pdf')
          .order('created_at', { ascending: false })
          .limit(1);
        
        if (documents && documents.length > 0) {
          rateConfirmationPath = documents[0].file_path;
          console.log('Found rate confirmation PDF:', rateConfirmationPath);
        }
      } catch (error) {
        console.log('No rate confirmation PDF found for this load');
      }

      const newDispatch = await createDispatch({
        loadNumber: load.load_number,
        driverId: truck.driver.id,
        driverName: `${truck.driver.first_name} ${truck.driver.last_name}`,
        driverPhone: truck.driver.phone_number || '',
        truckId: truck.id,
        truckNumber: truck.unit_number,
        pickupLocation: load.pickup_location || '',
        pickupCity: load.pickup_city || '',
        pickupState: load.pickup_state || '',
        pickupTime: load.pickup_date,
        deliveryLocation: load.delivery_location || '',
        deliveryCity: load.delivery_city || '',
        deliveryState: load.delivery_state || '',
        deliveryTime: load.delivery_date,
        distance: load.total_miles || 0,
        rate: load.total_rate || 0,
        assignedBy: 'Current User',
        status: dispatchType, // 'active' or 'planned'
        driverPay: typeof driverPay === 'number' ? driverPay : 0,
        rateConfirmationPath: rateConfirmationPath, // Pass the PDF path
      });

      // Send notification to all admins and dispatchers
      const { data: users } = await supabase
        .from('profiles')
        .select('id')
        .in('role', ['Superadmin', 'Admin', 'Dispatch'])
        .neq('id', user?.id); // Exclude current user

      console.log('Users to notify:', users);

      if (users && newDispatch) {
        for (const notifyUser of users) {
          console.log('Sending notification to user:', notifyUser.id);
          const result = await notifyNewDispatch({
            userId: notifyUser.id,
            dispatchId: newDispatch.id,
            dispatchNumber: newDispatch.dispatchNumber || 'New Dispatch',
            loadNumber: load.load_number,
            driverName: `${truck.driver.first_name} ${truck.driver.last_name}`
          });
          console.log('Notification result:', result);
        }
      }

      toast.success('Dispatch created successfully!');
      setShowCreateModal(false);
      setSelectedLoad(null);
      setSelectedTruck(null);
      setDispatchStep(1);
      setLoadSearchTerm('');
      setTruckSearchTerm('');
      loadDispatches();
    } catch (error) {
      console.error('Error creating dispatch:', error);
      toast.error('Failed to create dispatch');
    } finally {
      setDispatching(false);
    }
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      planned: 'bg-blue-100 text-blue-700 border-blue-300',
      active: 'bg-green-100 text-green-700 border-green-300',
      completed: 'bg-slate-100 text-slate-700 border-slate-300',
    };
    return colors[status.toLowerCase()] || 'bg-slate-100 text-slate-700 border-slate-300';
  };

  const filteredLoads = availableLoads.filter(load =>
    load.load_number.toLowerCase().includes(loadSearchTerm.toLowerCase()) ||
    load.commodity?.toLowerCase().includes(loadSearchTerm.toLowerCase()) ||
    load.pickup_city?.toLowerCase().includes(loadSearchTerm.toLowerCase()) ||
    load.delivery_city?.toLowerCase().includes(loadSearchTerm.toLowerCase())
  );

  const filteredTrucks = availableTrucks.filter(truck =>
    truck.unit_number.toLowerCase().includes(truckSearchTerm.toLowerCase()) ||
    `${truck.driver.first_name} ${truck.driver.last_name}`.toLowerCase().includes(truckSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search dispatches..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Dispatch
        </button>
      </div>

      {/* Status Summary & Filters */}
      <div className="flex items-center gap-3 mb-4">
        {/* Status Cards */}
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-2 rounded-lg border transition-all ${
            statusFilter === 'all' 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-slate-200 bg-white hover:border-blue-300'
          }`}
        >
          <span className="font-bold text-slate-900">{allDispatches.filter(d => d.status !== 'removed' && d.status !== 'archived').length}</span>
          <span className="text-xs text-slate-600 ml-1">Total</span>
        </button>
        <button
          onClick={() => setStatusFilter('active')}
          className={`px-4 py-2 rounded-lg border transition-all ${
            statusFilter === 'active' 
              ? 'border-green-500 bg-green-50' 
              : 'border-slate-200 bg-white hover:border-green-300'
          }`}
        >
          <span className="font-bold text-green-600">
            {allDispatches.filter(d => d.status !== 'removed' && d.status !== 'delivered' && d.status !== 'archived').length}
          </span>
          <span className="text-xs text-slate-600 ml-1">Active</span>
        </button>
        <button
          onClick={() => setStatusFilter('delivered')}
          className={`px-4 py-2 rounded-lg border transition-all ${
            statusFilter === 'delivered' 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-slate-200 bg-white hover:border-blue-300'
          }`}
        >
          <span className="font-bold text-blue-600">
            {allDispatches.filter(d => d.status === 'delivered').length}
          </span>
          <span className="text-xs text-slate-600 ml-1">Delivered</span>
        </button>
        <button
          onClick={() => setStatusFilter('archived')}
          className={`px-4 py-2 rounded-lg border transition-all ${
            statusFilter === 'archived' 
              ? 'border-purple-500 bg-purple-50' 
              : 'border-slate-200 bg-white hover:border-purple-300'
          }`}
        >
          <span className="font-bold text-purple-600">
            {allDispatches.filter(d => d.status === 'archived').length}
          </span>
          <span className="text-xs text-slate-600 ml-1">Archived</span>
        </button>
        <div className="h-8 w-px bg-slate-300 mx-2"></div>
        
        {/* Filter Dropdowns */}
        <select
          value={truckFilter}
          onChange={(e) => setTruckFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white hover:border-blue-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
        >
          <option value="all">All Trucks</option>
          {[...new Set(dispatches.map(d => d.truck_number))].map(truck => (
            <option key={truck} value={truck}>{truck}</option>
          ))}
        </select>
        
        <select
          value={driverFilter}
          onChange={(e) => setDriverFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white hover:border-blue-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
        >
          <option value="all">All Drivers</option>
          {[...new Set(dispatches.map(d => d.driver_name))].map(driver => (
            <option key={driver} value={driver}>{driver}</option>
          ))}
        </select>
        
        <select
          value={createdByFilter}
          onChange={(e) => setCreatedByFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white hover:border-blue-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
        >
          <option value="all">All Users</option>
          {[...new Set(dispatches.map(d => d.dispatcher_name))].map(user => (
            <option key={user} value={user}>{user}</option>
          ))}
        </select>
      </div>

      {/* Dispatches Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Created</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Created By</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Dispatch #</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Load</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Driver</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Truck</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Route</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(() => {
                // Filter dispatches
                const filteredDispatches = dispatches.filter(dispatch => {
                  // Note: Status filtering is already handled by filterDispatches() function
                  // Don't add duplicate status filtering here
                  
                  // Truck filter
                  if (truckFilter !== 'all' && dispatch.truck_number !== truckFilter) return false;
                  // Driver filter
                  if (driverFilter !== 'all' && dispatch.driver_name !== driverFilter) return false;
                  // Created By filter
                  if (createdByFilter !== 'all' && dispatch.dispatcher_name !== createdByFilter) return false;
                  // Search filter
                  if (searchTerm && !dispatch.dispatch_number.toLowerCase().includes(searchTerm.toLowerCase()) &&
                      !dispatch.load_ids.some(id => id.toLowerCase().includes(searchTerm.toLowerCase()))) return false;
                  return true;
                });

                // Calculate pagination
                const totalPages = Math.ceil(filteredDispatches.length / itemsPerPage);
                const startIndex = (currentPage - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const paginatedDispatches = filteredDispatches.slice(startIndex, endIndex);

                // Store for pagination controls
                (window as any).__dispatchPagination = { filteredDispatches, totalPages, startIndex, endIndex };

                if (paginatedDispatches.length === 0) {
                  return (
                    <tr>
                      <td colSpan={9} className="px-6 py-12 text-center text-slate-500">
                        <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p className="text-lg font-medium">No dispatches yet</p>
                        <p className="text-sm">Create your first dispatch to get started</p>
                      </td>
                    </tr>
                  );
                }

                return paginatedDispatches.map((dispatch) => {
                    // Determine row color based on status
                    let rowColor = 'hover:bg-slate-50';
                    if (dispatch.status === 'delivered') {
                      rowColor = 'bg-green-50 hover:bg-green-100';
                    } else if (dispatch.status === 'active') {
                      rowColor = 'bg-yellow-50 hover:bg-yellow-100';
                    } else if (dispatch.status === 'planned') {
                      rowColor = 'bg-blue-50 hover:bg-blue-100';
                    } else if (dispatch.status === 'late' || dispatch.status === 'delayed') {
                      rowColor = 'bg-red-50 hover:bg-red-100';
                    }
                    
                    return (<tr 
                      key={dispatch.id} 
                      className={`${rowColor} cursor-pointer`}
                      onClick={() => {
                        const newPath = `dispatches/${dispatch.id}`;
                        window.history.pushState({}, '', `/${newPath}`);
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      }}
                    >
                    <td className="px-4 py-2 text-xs text-slate-600">
                      {dispatch.created_at ? new Date(dispatch.created_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      }) : '-'}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600">
                      {dispatch.dispatcher_name || 'System'}
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => {
                          const newPath = `dispatches/${dispatch.id}`;
                          window.history.pushState({}, '', `/${newPath}`);
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                        className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {dispatch.dispatch_number}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-900">
                      {dispatch.load_ids.join(', ')}
                      {dispatch.is_tonu && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                          TONU
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-900">{dispatch.driver_name}</td>
                    <td className="px-4 py-2 text-xs text-slate-900">{dispatch.truck_number}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">
                      {dispatch.origin} → {dispatch.destination}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getStatusColor(dispatch.status)}`}>
                        {dispatch.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {dispatch.status !== 'delivered' && dispatch.status !== 'removed' && (
                          // Show complete button based on role permissions
                          (profile?.role === 'Superadmin' || profile?.role === 'superadmin' || profile?.role === 'Admin' || profile?.role === 'admin' || 
                           ((profile?.role === 'Dispatch' || profile?.role === 'Dispatch Manager') && dispatchPermissions.get(dispatch.id))) && (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                setDispatchToComplete(dispatch.id);
                                setShowCompletionConfirm(true);
                              }}
                              className="px-3 py-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-md shadow-sm hover:shadow transition-all flex items-center gap-1.5"
                              title="Mark as Complete"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Complete
                            </button>
                          )
                        )}
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            const newPath = `dispatches/edit/${dispatch.id}`;
                            window.history.pushState({}, '', `/${newPath}`);
                            window.dispatchEvent(new PopStateEvent('popstate'));
                          }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md"
                          title="Edit Dispatch"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        {dispatch.status !== 'removed' && (
                          // Show delete button for non-delivered or for admin/superadmin on delivered
                          (dispatch.status !== 'delivered' || profile?.role === 'Superadmin' || profile?.role === 'superadmin' || profile?.role === 'Admin' || profile?.role === 'admin') && (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                setDispatchToDelete(dispatch);
                                setShowDeleteConfirm(true);
                              }}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-md"
                              title="Remove Dispatch"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>);
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {(() => {
        const pagination = (window as any).__dispatchPagination;
        if (!pagination || pagination.totalPages <= 1) return null;
        
        const { filteredDispatches, totalPages, startIndex, endIndex } = pagination;
        
        return (
          <div className="mt-4 flex items-center justify-between px-4">
            <div className="text-sm text-slate-600">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredDispatches.length)} of {filteredDispatches.length} results
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'border border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-2 text-slate-400">...</span>;
                  }
                  return null;
                })}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        );
      })()}

      {/* Create Dispatch Modal - 2 Step Funnel */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header with Progress Indicator */}
            <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Create New Dispatch</h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setSelectedLoad(null);
                    setSelectedTruck(null);
                    setDispatchStep(1);
                    setLoadSearchTerm('');
                    setTruckSearchTerm('');
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Progress Steps */}
              <div className="flex items-center gap-2">
                <div className={`flex items-center gap-2 ${dispatchStep === 1 ? 'text-white' : 'text-blue-200'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                    dispatchStep === 1 ? 'bg-white text-blue-600' : 'bg-blue-500'
                  }`}>
                    1
                  </div>
                  <span className="font-medium text-sm">Select Load</span>
                </div>
                <div className="flex-1 h-0.5 bg-blue-400"></div>
                <div className={`flex items-center gap-2 ${dispatchStep === 2 ? 'text-white' : 'text-blue-200'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                    dispatchStep === 2 ? 'bg-white text-blue-600' : 'bg-blue-500'
                  }`}>
                    2
                  </div>
                  <span className="font-medium text-sm">Select Truck</span>
                </div>
                <div className="flex-1 h-0.5 bg-blue-400"></div>
                <div className={`flex items-center gap-2 ${dispatchStep === 3 ? 'text-white' : 'text-blue-200'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                    dispatchStep === 3 ? 'bg-white text-blue-600' : 'bg-blue-500'
                  }`}>
                    3
                  </div>
                  <span className="font-medium text-sm">Dispatch Details</span>
                </div>
              </div>
            </div>

            {/* Step 1: Select Load */}
            {dispatchStep === 1 && (
              <div className="flex-1 overflow-y-auto p-6">
                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search by load number, commodity, or location..."
                      value={loadSearchTerm}
                      onChange={(e) => setLoadSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Load Cards - Compact 2-Column Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {filteredLoads.map((load) => (
                    <button
                      key={load.id}
                      onClick={() => {
                        setSelectedLoad(load.id);
                        setDispatchStep(2);
                      }}
                      className="text-left p-3 border-2 rounded-lg transition-all hover:shadow-md border-slate-200 hover:border-blue-400 bg-white"
                    >
                      {/* Load Number & Rate */}
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-slate-900 text-sm">{load.load_number}</span>
                        <div className="text-right">
                          <div className="text-base font-bold text-green-600">
                            ${(load.total_rate || 0).toLocaleString()}
                          </div>
                          <div className="text-[10px] text-slate-500">
                            ${((load.total_rate || 0) / (load.total_miles || 1)).toFixed(2)}/mi
                          </div>
                        </div>
                      </div>

                      {/* Commodity */}
                      <div className="text-sm font-medium text-slate-900 mb-2">
                        {load.commodity || 'General Freight'}
                      </div>

                      {/* Route with Cities */}
                      <div className="flex items-center gap-1 text-xs text-slate-600 mb-2">
                        <MapPin className="w-3 h-3 text-blue-600 flex-shrink-0" />
                        <span className="font-medium truncate">
                          {load.pickup_city || 'Pickup'}, {load.pickup_state || 'XX'} → {load.delivery_city || 'Delivery'}, {load.delivery_state || 'XX'}
                        </span>
                      </div>

                      {/* Dates & Miles */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-[10px] text-slate-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>Pickup: {new Date(load.pickup_date).toLocaleDateString()}</span>
                          </div>
                          <span className="font-semibold text-blue-600">{load.total_miles || 0} mi</span>
                        </div>
                        <div className="text-[10px] text-slate-500">
                          Delivery: {new Date(load.delivery_date).toLocaleDateString()}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {filteredLoads.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Package className="w-16 h-16 mx-auto mb-3 text-slate-300" />
                    <p className="text-lg font-medium">No available loads</p>
                    <p className="text-sm">All loads have been dispatched</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Select Truck */}
            {dispatchStep === 2 && (
              <div className="flex-1 overflow-y-auto p-6">
                {/* Selected Load Summary */}
                {selectedLoad && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="text-sm text-blue-900">
                      <span className="font-semibold">Selected Load:</span>{' '}
                      {availableLoads.find(l => l.id === selectedLoad)?.load_number}
                      <span className="ml-3 text-blue-700">
                        {availableLoads.find(l => l.id === selectedLoad)?.pickup_city} →{' '}
                        {availableLoads.find(l => l.id === selectedLoad)?.delivery_city}
                      </span>
                    </div>
                  </div>
                )}

                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search by truck number or driver name..."
                      value={truckSearchTerm}
                      onChange={(e) => setTruckSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Truck Cards - Compact 2-Column Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {filteredTrucks.map((truck) => (
                    <button
                      key={truck.id}
                      onClick={() => setSelectedTruck(truck.id)}
                      className={`text-left p-3 border-2 rounded-lg transition-all ${
                        selectedTruck === truck.id
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-slate-200 hover:border-blue-400 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="bg-blue-100 p-1.5 rounded">
                          <Truck className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-slate-900 text-sm">{truck.unit_number}</p>
                          <div className="flex items-center gap-1.5">
                            <p className="text-[10px] text-slate-500">{truck.make} {truck.model}</p>
                            {truck.company && (
                              <>
                                <span className="text-[10px] text-slate-400">•</span>
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                  truck.company === 'APOLLO' ? 'text-blue-700 bg-blue-100' :
                                  truck.company === 'TESLA' ? 'text-purple-700 bg-purple-100' :
                                  truck.company === 'TMARK' ? 'text-green-700 bg-green-100' :
                                  'text-slate-700 bg-slate-100'
                                }`}>
                                  {truck.company}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-8">
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                          <span className="text-[10px] font-semibold text-green-700">
                            {truck.driver.first_name[0]}{truck.driver.last_name[0]}
                          </span>
                        </div>
                        <span className="text-xs text-slate-700">
                          {truck.driver.first_name} {truck.driver.last_name}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>

                {filteredTrucks.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Truck className="w-16 h-16 mx-auto mb-3 text-slate-300" />
                    <p className="text-lg font-medium">No available trucks</p>
                    <p className="text-sm">All trucks are currently assigned</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Dispatch Details */}
            {dispatchStep === 3 && (
              <div className="flex-1 overflow-y-auto p-6">
                <div className="max-w-2xl mx-auto space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Driver Pay
                      <span className="text-slate-500 ml-2 font-normal text-xs">
                        {selectedLoad && availableLoads.find(l => l.id === selectedLoad) && (
                          `(Load Rate: $${(availableLoads.find(l => l.id === selectedLoad)?.total_rate || 0).toFixed(2)})`
                        )}
                      </span>
                      {selectedDriverType && (
                        <span className={`ml-2 text-xs font-semibold ${selectedDriverType === 'company_driver' ? 'text-blue-600' : 'text-orange-600'}`}>
                          [{selectedDriverType === 'company_driver' ? 'Company Driver - Auto-filled' : 'Owner Operator - Required'}]
                        </span>
                      )}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 font-semibold">$</span>
                      <input
                        type="number"
                        value={driverPay}
                        onChange={(e) => setDriverPay(e.target.value === '' ? '' : Number(e.target.value))}
                        className={`w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          selectedDriverType === 'company_driver' ? 'bg-blue-50 border-blue-300' : 'border-slate-300'
                        }`}
                        placeholder="Enter driver pay amount"
                        min="0"
                        step="0.01"
                        readOnly={selectedDriverType === 'company_driver'}
                      />
                    </div>
                    {typeof driverPay === 'number' && driverPay > 0 && (() => {
                      const selectedLoadData = availableLoads.find(l => l.id === selectedLoad);
                      const loadRate = selectedLoadData?.total_rate || 0;
                      const exceedsRate = driverPay > loadRate;
                      
                      return (
                        <>
                          <p className={`mt-2 text-sm ${exceedsRate ? 'text-red-600' : 'text-green-600'}`}>
                            {exceedsRate ? '⚠️' : '✅'} Driver will receive: ${driverPay.toFixed(2)}
                          </p>
                          {exceedsRate && (
                            <p className="mt-1 text-sm text-red-600 font-semibold">
                              ⚠️ Warning: Driver pay (${driverPay.toFixed(2)}) exceeds load rate (${loadRate.toFixed(2)})
                            </p>
                          )}
                        </>
                      );
                    })()}
                    {selectedDriverType !== 'company_driver' && (driverPay === '' || driverPay === 0) && (
                      <p className="mt-2 text-sm text-orange-600">
                        ⚠️ Driver pay is required for owner operators
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Footer with Navigation */}
            <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-between">
              <div>
                {dispatchStep === 2 && (
                  <button
                    onClick={() => {
                      setDispatchStep(1);
                      setSelectedTruck(null);
                    }}
                    className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium flex items-center gap-2"
                  >
                    ← Back to Loads
                  </button>
                )}
                {dispatchStep === 3 && (
                  <button
                    onClick={() => setDispatchStep(2)}
                    className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium flex items-center gap-2"
                  >
                    ← Back to Trucks
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setSelectedLoad(null);
                    setSelectedTruck(null);
                    setDispatchStep(1);
                    setLoadSearchTerm('');
                    setTruckSearchTerm('');
                  }}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-white transition-colors"
                >
                  Cancel
                </button>
                {dispatchStep === 2 && (
                  <button
                    onClick={() => setDispatchStep(3)}
                    disabled={!selectedLoad || !selectedTruck}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next: Dispatch Details →
                  </button>
                )}
                {dispatchStep === 3 && (
                  <button
                    onClick={handleCreateDispatch}
                    disabled={dispatching || (selectedDriverType !== 'company_driver' && (driverPay === '' || driverPay === 0))}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {dispatching ? 'Creating...' : 'Create Dispatch'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dispatch Confirmation */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Remove Dispatch"
        message={dispatchToDelete
          ? `Are you sure you want to remove dispatch ${dispatchToDelete.dispatch_number}?\n\n⚠️ This will:\n• Mark the dispatch as "Removed"\n• Exclude it from analytics and revenue calculations\n• Set the load back to "Booked" status\n\nYou can view removed dispatches in the "Removed" filter.`
          : 'Are you sure you want to remove this dispatch?'}
        confirmText="Remove Dispatch"
        cancelText="Cancel"
        onConfirm={handleDeleteDispatch}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDispatchToDelete(null);
        }}
        type="danger"
      />

      {/* Completion Confirmation Modal */}
      {showCompletionConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-green-600 p-6 rounded-t-xl flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white">Confirm Delivery</h3>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 text-center mb-2 text-lg font-semibold">
                Are you sure this load is delivered?
              </p>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mt-4 rounded">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <span className="font-semibold">⚠️ Warning:</span> Marking this load as complete <span className="font-semibold">cannot be undone</span>. The load will be marked as delivered. To bill it, open the load and use <span className="font-semibold">Send to Accounting</span>, which also archives it.
                </p>
              </div>
            </div>

            {/* Footer with buttons */}
            <div className="bg-gray-50 px-6 py-4 flex gap-3">
              <button
                onClick={() => {
                  setShowCompletionConfirm(false);
                  setDispatchToComplete(null);
                }}
                className="flex-1 px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={confirmCompleteDispatch}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg hover:from-emerald-700 hover:to-green-700 transition-all font-semibold shadow-lg"
              >
                Yes, Mark as Delivered
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
